import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NavBar from '../../components/NavBar';
import Spinner from '../../components/Spinner';
import AvatarFallback from '../../components/AvatarFallback/AvatarFallback';
import GlassCapsulePopup from '../../components/GlassCapsulePopup';
import UniversitySearch from '../../components/UniversitySearch/UniversitySearch';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { SUPPORTED_UNIVERSITY_IDS } from '../../config/supportedUniversities';
import './EditProfile.css';

interface University {
    id: number;
    name: string;
    abbreviation: string;
    location: string;
}

interface UserProfile {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    university?: string;
    university_id?: number;
    university_abbreviation?: string;
    avatar_url?: string;
    instagram?: string;
    created_at: string;
    email_verification_status: 'unverified' | 'verified';
}

const EditProfile: React.FC = () => {
    const navigate = useNavigate();
    const { user, token, isAuthenticated, isLoading: authLoading, refreshUserProfile, updateProfile, logout } = useAuth();

    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [avatarRetryCount, setAvatarRetryCount] = useState(0);
    const [avatarRefreshKey, setAvatarRefreshKey] = useState(0);

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        instagram: '',
        university_id: '',
        university: '',
        email: '',
    });

    const [view, setView] = useState<'edit' | 'changePassword'>('edit');
    const [changePasswordData, setChangePasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [changePasswordError, setChangePasswordError] = useState<string | null>(null);

    const [showSuccess, setShowSuccess] = useState(false);
    const [showAvatarSuccess, setShowAvatarSuccess] = useState(false);

    // ─── Fetch helpers ────────────────────────────────────────────────────────

    const fetchProfile = useCallback(async () => {
        try {
            const authToken = token || localStorage.getItem('auth_token');
            if (!authToken) return;

            const response = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                headers: {
                    Authorization: `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const responseData = await response.json();
                const profileData = responseData.data || responseData;

                if (!profileData || !profileData.email) {
                    if (user) {
                        const fallback: UserProfile = {
                            id: user.id || '',
                            email: user.email || '',
                            first_name: user.first_name || '',
                            last_name: user.last_name || '',
                            university: user.university || '',
                            university_id: undefined,
                            avatar_url: user.avatar_url || '',
                            created_at: user.created_at || '',
                            email_verification_status: user.email_verification_status || 'unverified',
                        };
                        setProfile(fallback);
                        setFormData({
                            first_name: fallback.first_name || '',
                            last_name: fallback.last_name || '',
                            instagram: fallback.instagram || '',
                            university_id: fallback.university_id?.toString() || '',
                            university: fallback.university || '',
                            email: fallback.email || '',
                        });
                    }
                } else {
                    setProfile(profileData);
                    setFormData({
                        first_name: profileData.first_name || '',
                        last_name: profileData.last_name || '',
                        instagram: profileData.instagram || '',
                        university_id: profileData.university_id?.toString() || '',
                        university: profileData.university || '',
                        email: profileData.email || '',
                    });
                }
            } else if (response.status === 401) {
                logout();
                navigate('/');
            }
        } catch {
            setError('Network error loading profile.');
        } finally {
            setLoading(false);
        }
    }, [token, user, logout, navigate]);


    useEffect(() => {
        if (authLoading) return;
        if (isAuthenticated && token) {
            fetchProfile();
        } else {
            setLoading(false);
            navigate('/');
        }
    }, [authLoading, isAuthenticated, token, navigate, fetchProfile]);

    // ─── Avatar upload ────────────────────────────────────────────────────────

    const compressAvatarImage = (file: File, maxW = 400, maxH = 400, quality = 0.75): Promise<File> =>
        new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            let objectUrl: string | null = null;

            img.onload = () => {
                let { width, height } = img;
                if (width > maxW || height > maxH) {
                    const ratio = Math.min(maxW / width, maxH / height);
                    width *= ratio;
                    height *= ratio;
                }
                canvas.width = width;
                canvas.height = height;
                ctx?.drawImage(img, 0, 0, width, height);
                canvas.toBlob(
                    (blob) => {
                        if (objectUrl) URL.revokeObjectURL(objectUrl);
                        resolve(blob ? new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }) : file);
                    },
                    'image/jpeg',
                    quality
                );
            };
            img.onerror = () => {
                if (objectUrl) URL.revokeObjectURL(objectUrl);
                resolve(file);
            };
            objectUrl = URL.createObjectURL(file);
            img.src = objectUrl;
        });

    const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { setError('Please select an image file'); return; }
        if (file.size > 10 * 1024 * 1024) { setError('File size must be less than 10MB'); return; }

        setIsUploadingAvatar(true);
        setError(null);

        try {
            let compressed = await compressAvatarImage(file, 400, 400, 0.75);
            const toBase64 = (f: File): Promise<string> =>
                new Promise((res, rej) => {
                    const reader = new FileReader();
                    reader.onload = () => res(reader.result as string);
                    reader.onerror = () => rej(reader.error);
                    reader.readAsDataURL(f);
                });

            let base64 = await toBase64(compressed);
            if (base64.length / (1024 * 1024) > 5) {
                compressed = await compressAvatarImage(file, 300, 300, 0.65);
                base64 = await toBase64(compressed);
            }
            if (base64.length / (1024 * 1024) > 6) {
                setError('Image is still too large after compression. Please try a smaller image.');
                return;
            }

            const authToken = token || localStorage.getItem('auth_token');
            const response = await fetch(`${API_BASE_URL}/api/v1/users/avatar-base64`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ image_data: base64 }),
            });

            if (response.ok) {
                const result = await response.json();
                await new Promise((r) => setTimeout(r, 500));
                await fetchProfile();
                if (refreshUserProfile) await refreshUserProfile();
                setProfile((prev) => {
                    if (prev) {
                        setAvatarRetryCount(0);
                        return { ...prev, avatar_url: result.avatar_url };
                    }
                    return prev;
                });
                if (user) updateProfile({ avatar_url: result.avatar_url });
                setAvatarRefreshKey((k) => k + 1);
                setTimeout(() => setAvatarRefreshKey(0), 10000);
                setShowAvatarSuccess(true);
                event.target.value = '';
            } else {
                const errData = await response.json().catch(() => ({}));
                setError(errData.detail || `Upload failed (${response.status})`);
            }
        } catch {
            setError('Failed to upload avatar. Please try again.');
        } finally {
            setIsUploadingAvatar(false);
        }
    };

    // ─── Save profile ─────────────────────────────────────────────────────────

    const handleSave = async () => {
        setError(null);
        try {
            const authToken = token || localStorage.getItem('auth_token');
            if (!authToken) { setError('No authentication token found.'); return; }

            if (formData.email) {
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.email)) { setError('Please enter a valid email address'); return; }
            }

            const submitData: Record<string, unknown> = {
                first_name: formData.first_name,
                last_name: formData.last_name,
                university_id: formData.university_id ? parseInt(formData.university_id) : null,
                university: formData.university || null,
                instagram: formData.instagram.trim() || null,
            };
            if (formData.email && formData.email !== profile?.email) {
                submitData.email = formData.email;
            }

            const response = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                body: JSON.stringify(submitData),
            });

            if (response.ok) {
                if (refreshUserProfile) await refreshUserProfile();
                setShowSuccess(true);
                setTimeout(() => navigate('/profile'), 1200);
            } else if (response.status === 401) {
                logout();
                navigate('/');
            } else {
                const errData = await response.json();
                setError(errData.detail || 'Failed to update profile');
            }
        } catch {
            setError('Network error occurred');
        }
    };

    const handleCancel = () => {
        navigate('/profile');
    };

    // ─── Change password ──────────────────────────────────────────────────────

    const handleChangePasswordCancel = () => {
        setView('edit');
        setChangePasswordData({ current_password: '', new_password: '', confirm_password: '' });
        setChangePasswordError(null);
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
    };

    const handleChangePassword = async () => {
        setChangePasswordError(null);
        try {
            const authToken = token || localStorage.getItem('auth_token');
            if (!authToken) { setChangePasswordError('No authentication token found.'); return; }
            if (!changePasswordData.current_password) { setChangePasswordError('Current password is required'); return; }
            if (!changePasswordData.new_password) { setChangePasswordError('New password is required'); return; }
            if (changePasswordData.new_password.length < 6) { setChangePasswordError('New password must be at least 6 characters'); return; }
            if (changePasswordData.new_password !== changePasswordData.confirm_password) { setChangePasswordError('New passwords do not match'); return; }

            const response = await fetch(API_ENDPOINTS.USERS.PROFILE, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
                body: JSON.stringify({
                    current_password: changePasswordData.current_password,
                    new_password: changePasswordData.new_password,
                }),
            });

            if (response.ok) {
                setChangePasswordData({ current_password: '', new_password: '', confirm_password: '' });
                setShowCurrentPassword(false);
                setShowNewPassword(false);
                setShowConfirmPassword(false);
                setView('edit');
                setShowSuccess(true);
                if (refreshUserProfile) await refreshUserProfile();
            } else if (response.status === 401) {
                logout();
                navigate('/');
            } else {
                const errData = await response.json();
                setChangePasswordError(errData.detail || 'Failed to change password');
            }
        } catch {
            setChangePasswordError('Network error occurred');
        }
    };

    // ─── Eye icon SVG helpers ─────────────────────────────────────────────────

    const EyeOff = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
    );
    const EyeOn = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
        </svg>
    );

    // ─── Render ───────────────────────────────────────────────────────────────

    if (authLoading || loading) {
        return (
            <div className="edit-profile-page">
                <NavBar />
                <div className="edit-profile-page-loading">
                    <div className="profile-loading-dots" aria-label="Loading" role="status">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="edit-profile-page">
            <NavBar />

            <div className="edit-profile-page-content">
                <div className="edit-profile-page-card">

                    {/* Header — only shown on Change Password view */}
                    {view === 'changePassword' && (
                        <div className="edit-profile-page-header">
                            <button
                                className="edit-profile-back-btn"
                                onClick={handleChangePasswordCancel}
                                aria-label="Back to edit profile"
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M19 12H5M12 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <h1 className="edit-profile-page-title">Change Password</h1>
                        </div>
                    )}

                    {/* ── Edit view ── */}
                    {view === 'edit' ? (
                        <>
                            {/* Avatar */}
                            <div className="edit-profile-avatar-section">
                                <div className="edit-profile-avatar">
                                    <input
                                        type="file"
                                        id="ep-avatar-upload"
                                        accept="image/*"
                                        onChange={handleAvatarUpload}
                                        style={{ display: 'none' }}
                                        disabled={isUploadingAvatar}
                                    />
                                    <label htmlFor="ep-avatar-upload" className="edit-avatar-upload-label">
                                        {profile?.avatar_url ? (
                                            <img
                                                key={`${profile.avatar_url}-${avatarRefreshKey}`}
                                                src={
                                                    profile.avatar_url.startsWith('http')
                                                        ? avatarRefreshKey > 0
                                                            ? `${profile.avatar_url}?v=${avatarRefreshKey}`
                                                            : profile.avatar_url
                                                        : avatarRefreshKey > 0
                                                        ? `${API_BASE_URL}${profile.avatar_url}?v=${avatarRefreshKey}`
                                                        : `${API_BASE_URL}${profile.avatar_url}`
                                                }
                                                alt={`${profile?.first_name} ${profile?.last_name}`}
                                                onError={(e) => {
                                                    const img = e.currentTarget;
                                                    img.style.display = 'none';
                                                    const placeholder = img.nextElementSibling as HTMLElement;
                                                    if (placeholder) placeholder.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <AvatarFallback
                                            firstName={profile?.first_name}
                                            lastName={profile?.last_name}
                                            email={profile?.email}
                                            size={120}
                                            className="edit-avatar-placeholder"
                                            style={{
                                                display: profile?.avatar_url ? 'none' : 'flex',
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: '16px',
                                                fontSize: '2rem',
                                                backgroundColor: '#f3f4f6',
                                                background: '#f3f4f6',
                                                color: '#111827',
                                            }}
                                        />
                                        <div className={`edit-avatar-overlay ${isUploadingAvatar ? 'uploading' : ''}`}>
                                            {isUploadingAvatar ? (
                                                <Spinner size="small" color="green" />
                                            ) : (
                                                <div className="pencil-icon">✏</div>
                                            )}
                                        </div>
                                    </label>
                                </div>
                            </div>

                            {/* Form */}
                            <form
                                className="edit-profile-form"
                                onSubmit={(e) => { e.preventDefault(); handleSave(); }}
                            >
                                <div className="edit-profile-form-row">
                                    <div className="edit-profile-form-group">
                                        <label htmlFor="ep-first-name">First Name</label>
                                        <input
                                            type="text"
                                            id="ep-first-name"
                                            name="first_name"
                                            value={formData.first_name}
                                            onChange={(e) => setFormData((p) => ({ ...p, first_name: e.target.value }))}
                                            placeholder="First Name"
                                        />
                                    </div>
                                    <div className="edit-profile-form-group">
                                        <label htmlFor="ep-last-name">Last Name</label>
                                        <input
                                            type="text"
                                            id="ep-last-name"
                                            name="last_name"
                                            value={formData.last_name}
                                            onChange={(e) => setFormData((p) => ({ ...p, last_name: e.target.value }))}
                                            placeholder="Last Name"
                                        />
                                    </div>
                                </div>
                                <div className="edit-profile-form-group">
                                    <label htmlFor="ep-instagram">Instagram <span style={{ fontWeight: 400, fontSize: '0.85em', color: 'var(--text-secondary)' }}>(optional)</span></label>
                                    <input
                                        type="text"
                                        id="ep-instagram"
                                        name="instagram"
                                        value={formData.instagram}
                                        onChange={(e) => setFormData((p) => ({ ...p, instagram: e.target.value.replace(/^@/, '') }))}
                                        placeholder="Your Instagram username"
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="edit-profile-form-group">
                                    <label htmlFor="ep-university">University or City <span style={{ fontWeight: 400, fontSize: '0.85em', color: 'var(--text-secondary)' }}>(optional)</span></label>
                                    <UniversitySearch
                                        value={formData.university_id}
                                        onChange={(value) => setFormData(prev => ({ ...prev, university_id: value }))}
                                        onInputChange={(name) => setFormData(prev => ({ ...prev, university: name }))}
                                        placeholder="University or City (optional)"
                                        className="edit-profile-university-search"
                                        disableCapsuleClick={true}
                                        disableCapsule={true}
                                        showCities={true}
                                        supportedUniversityIds={SUPPORTED_UNIVERSITY_IDS}
                                    />
                                </div>
                                <div className="edit-profile-form-group">
                                    <label htmlFor="ep-email">Email</label>
                                    <input
                                        type="email"
                                        id="ep-email"
                                        name="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                                        placeholder="Enter your email"
                                    />
                                </div>
                                <div className="edit-profile-form-group">
                                    <button
                                        type="button"
                                        className="change-password-btn"
                                        onClick={() => setView('changePassword')}
                                    >
                                        Change Password
                                    </button>
                                </div>

                                {error && <div className="edit-profile-error">{error}</div>}

                                <div className="edit-profile-actions">
                                    <button type="button" className="edit-profile-cancel-btn" onClick={handleCancel}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="edit-profile-save-btn">
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        </>
                    ) : (
                        /* ── Change Password view ── */
                        <form
                            className="edit-profile-form"
                            onSubmit={(e) => { e.preventDefault(); handleChangePassword(); }}
                        >
                            <div className="edit-profile-form-group password-input-wrapper">
                                <label htmlFor="ep-current-pw">Current Password</label>
                                <input
                                    type={showCurrentPassword ? 'text' : 'password'}
                                    id="ep-current-pw"
                                    value={changePasswordData.current_password}
                                    onChange={(e) => setChangePasswordData((p) => ({ ...p, current_password: e.target.value }))}
                                    placeholder="Enter current password"
                                    maxLength={50}
                                    required
                                />
                                <button type="button" className="password-toggle-btn" onClick={() => setShowCurrentPassword(!showCurrentPassword)} aria-label={showCurrentPassword ? 'Hide' : 'Show'}>
                                    {showCurrentPassword ? <EyeOff /> : <EyeOn />}
                                </button>
                            </div>
                            <div className="edit-profile-form-group password-input-wrapper">
                                <label htmlFor="ep-new-pw">New Password</label>
                                <input
                                    type={showNewPassword ? 'text' : 'password'}
                                    id="ep-new-pw"
                                    value={changePasswordData.new_password}
                                    onChange={(e) => setChangePasswordData((p) => ({ ...p, new_password: e.target.value }))}
                                    placeholder="Enter new password (6-50 characters)"
                                    minLength={6}
                                    maxLength={50}
                                    required
                                />
                                <button type="button" className="password-toggle-btn" onClick={() => setShowNewPassword(!showNewPassword)} aria-label={showNewPassword ? 'Hide' : 'Show'}>
                                    {showNewPassword ? <EyeOff /> : <EyeOn />}
                                </button>
                            </div>
                            <div className="edit-profile-form-group password-input-wrapper">
                                <label htmlFor="ep-confirm-pw">Confirm New Password</label>
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="ep-confirm-pw"
                                    value={changePasswordData.confirm_password}
                                    onChange={(e) => setChangePasswordData((p) => ({ ...p, confirm_password: e.target.value }))}
                                    placeholder="Confirm new password"
                                    maxLength={50}
                                    minLength={6}
                                    required
                                />
                                <button type="button" className="password-toggle-btn" onClick={() => setShowConfirmPassword(!showConfirmPassword)} aria-label={showConfirmPassword ? 'Hide' : 'Show'}>
                                    {showConfirmPassword ? <EyeOff /> : <EyeOn />}
                                </button>
                            </div>

                            {changePasswordError && <div className="edit-profile-error">{changePasswordError}</div>}

                            <div className="edit-profile-actions">
                                <button type="button" className="edit-profile-cancel-btn" onClick={handleChangePasswordCancel}>
                                    Cancel
                                </button>
                                <button type="submit" className="edit-profile-save-btn">
                                    Change Password
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            <GlassCapsulePopup message="Profile updated successfully" show={showSuccess} onClose={() => setShowSuccess(false)} autoHideDuration={3000} />
            <GlassCapsulePopup message="Avatar updated successfully" show={showAvatarSuccess} onClose={() => setShowAvatarSuccess(false)} autoHideDuration={3000} />
        </div>
    );
};

export default EditProfile;
